import React, { useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSalesFormStore } from "../../services/useSalesFormStore";
import {
  buildTransactionDocumentFileName,
  capitalizeFirstLetterOfEachWord,
  convertToUpperCase,
} from "../../utils/Formatter";
import DownloadButton from "../../components/DownloadButton";
import { HrInductionTrainingSheetLayout } from "../../components/transactionPrintLayouts/HrInductionTrainingSheetLayout";
import { HrTrainingNeedsRecordLayout } from "../../components/transactionPrintLayouts/HrTrainingNeedsRecordLayout";
import { HrEmployeeHistoryCardLayout } from "../../components/transactionPrintLayouts/HrEmployeeHistoryCardLayout";
import "../../styles/pages/invoice/Invoice.css";

const TransactionInvoice = () => {
  const { tab, category } = useParams();
  const location = useLocation();
  const storeFormData = useSalesFormStore((state) => state.formData);
  const navigationFormData = location.state?.formData;

  const companyData = useMemo(
    () => navigationFormData?.companyData || storeFormData?.companyData || {},
    [navigationFormData, storeFormData]
  );
  const rowData = useMemo(
    () => navigationFormData?.rowData || storeFormData?.rowData || [],
    [navigationFormData, storeFormData]
  );

  const { headDetails, itemDetails } = useMemo(() => {
    if (navigationFormData) {
      return {
        headDetails: navigationFormData.headDetails || {},
        itemDetails: navigationFormData.itemDetails || [],
      };
    }
    return {
      headDetails: storeFormData?.headDetails || {},
      itemDetails: storeFormData?.itemDetails || [],
    };
  }, [navigationFormData, storeFormData]);

  const invoiceTableData = useMemo(() => {
    if (!rowData.length || !itemDetails.length) {
      return null;
    }

    const visibleFields = rowData.filter((field) => !field.is_internal_field);

    return {
      headings: visibleFields.map((field) => ({
        display_name: field.title || field.field_name,
        field_name: field.field_name,
        is_internal: false,
      })),
      downloadFileName: buildTransactionDocumentFileName(headDetails),
      rows: itemDetails,
    };
  }, [rowData, itemDetails, headDetails]);

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0.00";
    return Number(amount).toFixed(2);
  };

  const categoryValue = String(category || "").toLowerCase();
  const transactionCodeValue = String(
    navigationFormData?.transactionCode || "",
  ).toUpperCase();

  const isHrInductionTrainingSheetForm =
    ["HR_INDUCTION_TRAINING_SHEET", "INDUCTION_TRAINING_SHEET", "HR_ITS", "ITS"].includes(
      transactionCodeValue,
    ) || categoryValue.includes("induction-training-sheet");

  const isHrTrainingNeedsRecordForm =
    ["HR_TRAINING_NEEDS_RECORD", "TRAINING_NEEDS_RECORD", "HR_TNR", "TNR"].includes(
      transactionCodeValue,
    ) || categoryValue.includes("training-needs-record");

  const isHrEmployeeHistoryCardForm =
    ["HR_EMPLOYEE_HISTORY_CARD", "EMPLOYEE_HISTORY_CARD", "HR_EHC", "EHC"].includes(
      transactionCodeValue,
    ) || categoryValue.includes("employee-history-card");

  if (isHrInductionTrainingSheetForm) {
    return (
      <HrInductionTrainingSheetLayout
        headDetails={headDetails}
        companyData={companyData}
        itemDetails={itemDetails}
      />
    );
  }

  if (isHrTrainingNeedsRecordForm) {
    return (
      <HrTrainingNeedsRecordLayout
        headDetails={headDetails}
        companyData={companyData}
        itemDetails={itemDetails}
      />
    );
  }

  if (isHrEmployeeHistoryCardForm) {
    return (
      <HrEmployeeHistoryCardLayout
        headDetails={headDetails}
        companyData={companyData}
        itemDetails={itemDetails}
      />
    );
  }

  return (
    <div className="enquiry-page">
      <div className="enquiry-page-container">
        <div className="enquiry-header">
          <h2>
            {capitalizeFirstLetterOfEachWord(headDetails.tran_code || "")}
          </h2>
        </div>
        <div className="enquiry-details">
          <div className="details-heding">
            {capitalizeFirstLetterOfEachWord(category)} Details
          </div>
          <h3>
            {convertToUpperCase(tab)} {convertToUpperCase(category)}
          </h3>
          <div className="section">
            <div className="details-row">
              <div className="detail-group">
                <label className="semibold">Company Details</label>
                <div>
                  <p>{companyData.company_name || "N/A"}</p>
                  <p>
                    <span className="semibold">Phone: </span>
                    <span className="underline">
                      {companyData.company_phone || "N/A"}
                    </span>
                  </p>
                  <p>
                    <span className="semibold underline">Address: </span>
                    {companyData.company_address || "N/A"}
                  </p>
                </div>
              </div>

              <div className="detail-group">
                <label className="semibold">Delivery Details</label>
                <div>
                  <p>
                    <span className="semibold underline">Address: </span>
                    {companyData.delivery_address || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h2>Transaction Details</h2>
          <div className="details-row">
            <div className="details-group">
              <div className="flex-direction-row">
                <label>
                  Transaction Code - {headDetails.tran_code || "N/A"}
                </label>
              </div>
              <div className="flex-direction-row">
                <label>
                  Created Date - {formatDate(headDetails.created_at)}
                </label>
              </div>
              <div className="flex-direction-row">
                <label>Status - {headDetails.status || "N/A"}</label>
              </div>
            </div>

            <div className="details-group">
              <div className="flex-direction-row">
                <label>
                  Updated Date - {formatDate(headDetails.updated_at)}
                </label>
              </div>
              <div className="flex-direction-row">
                <label>
                  Gross Amount - Rs {formatCurrency(headDetails.gross_amt)}
                </label>
              </div>
              <div className="flex-direction-row">
                <label>
                  Net Amount - Rs {formatCurrency(headDetails.net_amt)}
                </label>
              </div>
            </div>
          </div>

          <div className="section">
            <table className="invoice-item-table">
              <thead>
                <tr>
                  <th>No.</th>
                  {[...rowData]
                    .filter((field) => !field.is_internal_field)
                    .reverse()
                    .map((field, index) => (
                      <th key={index}>{field.title || field.field_name}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {itemDetails.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    {[...rowData]
                      .filter((field) => !field.is_internal_field)
                      .reverse()
                      .map((field, idx) => (
                        <td key={idx}>{item[field.field_name] || "-"}</td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="totals">
              <p>Gross Amount: Rs {formatCurrency(headDetails.gross_amt)}</p>
              <p>Net Amount: Rs {formatCurrency(headDetails.net_amt)}</p>
              <div className="total-bill-separator"></div>
              <p className="semibold">
                Grand Total: Rs {formatCurrency(headDetails.net_amt)}
              </p>
            </div>

            <div className="signature">
              <span>Signature</span>
              <p>Created by: {headDetails.created_by || "N/A"}</p>
              {headDetails.approved_by && (
                <p>Approved by: {headDetails.approved_by}</p>
              )}
            </div>

            {/* <div className="invoice-buttons">
              <button className="save-draft">SAVE DRAFT</button>
              <button className="save-send">SAVE & SEND</button>
            </div> */}
          </div>
          <div className="invoice-buttons">
            <DownloadButton
              tableData={invoiceTableData}
              fileName="invoice"
              downloadFormats={["excel", "pdf", "full-page-pdf"]}
              fullPageDownload={true}
              fullPageSelector=".enquiry-page-container"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionInvoice;
